/**
 * Central schema registry for all CRUD form field types.
 *
 * Add a new resource here to configure its form behavior without touching
 * any other file. Every map is keyed by resource name (PostgREST table name
 * or the resource name used in RESOURCE_GROUPS).
 */

// ─── Countries ───────────────────────────────────────────────────────────────

export const COUNTRIES: string[] = [
  'Afghanistan', 'Albania', 'Algeria', 'Angola', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia',
  'Bosnia and Herzegovina', 'Brazil', 'Bulgaria', 'Cambodia', 'Cameroon', 'Canada',
  'Chile', 'China', 'Colombia', 'Congo', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Denmark', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Estonia',
  'Ethiopia', 'Finland', 'France', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala',
  'Honduras', 'Hungary', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kuwait', 'Kyrgyzstan', 'Latvia', 'Lebanon', 'Libya', 'Lithuania', 'Luxembourg',
  'Malaysia', 'Mali', 'Mexico', 'Moldova', 'Morocco', 'Mozambique', 'Myanmar',
  'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Nigeria', 'North Korea',
  'Norway', 'Oman', 'Pakistan', 'Palestine', 'Panama', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia',
  'Somalia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sudan',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
  'Tunisia', 'Turkey', 'Turkmenistan', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
]

// ─── Shared enum option lists ─────────────────────────────────────────────────

const EMPLOYMENT_TYPES = ['Full-Time', 'Part-Time', 'Freelance', 'Project-Based', 'Internship']
const WORKPLACE_TYPES  = ['On-Site', 'Remote', 'Hybrid']
const SENIORITY_LEVELS = ['Student', 'Entry Level', 'Experienced', 'Manager', 'Senior Manager', 'Not Specified']
const EDUCATION_LEVELS = ['Undergraduate', 'Bachelor', "Master's Degree", 'Doctorate Degree']
const TRAVEL_FREQS     = ['100%', '50%', '30%', '0%']
const HIRING_PRIORITIES = ['Low', 'Medium', 'High']
const INTERVIEW_LANGS  = ['AR', 'EN', 'DE']
const CURRENCIES       = ['USD', 'CAD', 'GBP', 'EUR', 'SAR', 'QAR', 'AED', 'EGP']
const GENDERS          = ['Male', 'Female', 'NotMentioned']
const LANGUAGE_LEVELS  = ['Basic', 'Intermediate', 'Advanced']

// ─── Boolean fields per resource ─────────────────────────────────────────────

/**
 * Fields that should render as a checkbox toggle.
 * Extend this map when adding new resources with boolean columns.
 */
export const BOOLEAN_FIELDS: Record<string, string[]> = {
  users:          ['email_verified', 'active'],
  identity:       ['is_social'],
  user_tokens:    ['is_valid'],
  companies: [
    'is_active', 'is_established', 'is_blocked', 'is_agency',
    'use_custom_email_config', 'edit_position_config', 'is_onboarding',
    'enable_voice_over', 'enable_google_calendar_integration',
    'enable_outlook_calendar_integration', 'enable_linkedin_integration',
    'use_livekit_video_service', 'show_client_details',
  ],
  branches:    ['is_default'],
  departments: ['is_default'],
  company_users: ['is_hr_manager', 'is_employee'],
  company_roles: ['is_protected', 'disable_delete'],
  company_email_configuration: ['mail_use_ssl', 'mail_use_tls'],
  local_candidates: ['is_parsed', 'is_blacklisted', 'is_blocked', 'is_revised_by_candidate'],
  job_seekers:  ['phone_verified', 'email_verified', 'open_for_hiring'],
  candidate_experiences: ['is_present'],
  candidate_documents:   ['is_resume', 'is_national_id', 'is_certificate'],
  company_positions: [
    'hide_salary', 'enable_auto_cover_letter', 'enable_email_notif',
    'enable_whatsapp_notif', 'enable_linkedin', 'disable_ai_questions',
    'per_question_interview_time', 'interview_reschedule',
  ],
  position_stages:    ['editable', 'deletable', 'is_default_interview'],
  position_questions: ['added_by_ai'],
  requisitions: [
    'hide_salary', 'enable_internal_hiring', 'enable_default_eliminating',
    'is_submitted', 'is_closed',
  ],
  interview_versions:   ['is_booked', 'is_started', 'is_declined', 'is_ended', 'bot_interview'],
  evaluation_forms:     ['is_dynamic'],
  voice_over_audit_logs: ['success'],
  subscriptions:         ['active'],
  plans:                 ['is_public', 'is_auto_renew'],
  feature_flags:         ['enabled'],

  tokens:                ['is_used'],
  onboarding_services:   ['is_active'],
  newsletter_subscriptions:      ['is_active'],
  blacklisted_emails:            ['is_active'],
  public_api_signing_challenges: ['is_used'],
  approval_requests: [],
  whatsapp_messages: [],
}

// ─── Enum fields per resource ─────────────────────────────────────────────────

/**
 * Fields that render as a single-value select from a fixed option list.
 * Key: resource name → field name → options array.
 */
export type EnumOption = string | { group: string; items: string[] }
export const ENUM_FIELDS: Record<string, Record<string, EnumOption[]>> = {
  companies: {
    unit_naming_type: ['branch_department', 'central_local', 'project_squad', 'department_unit'],
  },
  agencies: {
    type: ['Agency & Clients', 'Holding & Subsidiaries', 'Main Operator & Companies', 'Team & Depratments'],
    job_seeker_registration_method: ['email', 'phone'],
  },
  local_candidates: {
    gender: GENDERS,
    status: ['closed', 'expired', 'completed', 'on_hold'],
  },
  job_seekers: {
    currency: CURRENCIES,
    gender: GENDERS,
  },
  candidate_languages: {
    level: LANGUAGE_LEVELS,
  },
  company_positions: {
    employment_type:  EMPLOYMENT_TYPES,
    workplace_type:   WORKPLACE_TYPES,
    seniority_level:  SENIORITY_LEVELS,
    education_level:  EDUCATION_LEVELS,
    travel_freq:      TRAVEL_FREQS,
    hiring_priority:  HIRING_PRIORITIES,
    interview_lang:   INTERVIEW_LANGS,
    currency:         CURRENCIES,
    job_status:       ['draft', 'active', 'closed'],
    archive_reason:   ['closed', 'expired', 'completed', 'on_hold'],
  },
  position_stages: {
    stage_type: [
      'sourcing', 'noAuto', 'document', 'document collection', 'interview',
      'text interview', 'video interview', 'form interview', 'in-person interview',
      'in-person', 'cover letter interview', 'decision', 'evaluation', 'offer',
      'external onboarding', 'generic personal interview', 'reference',
    ],
  },
  requisitions: {
    employment_type:      EMPLOYMENT_TYPES,
    workplace_type:       WORKPLACE_TYPES,
    seniority_level:      SENIORITY_LEVELS,
    education_level:      EDUCATION_LEVELS,
    travel_freq:          TRAVEL_FREQS,
    hiring_priority:      HIRING_PRIORITIES,
    currency:             CURRENCIES,
    req_reason:           ['Expansion', 'Replacement', 'Job Holder Transfer', 'Job Holder Demotion', 'Job Holder Promotion'],
    req_status:           ['Requisition Created', 'Draft', 'Pending', 'Approved', 'Rejected', 'Filled', 'On Hold', 'Empty'],
    approval_status:      ['Requisition Created', 'Draft', 'Pending', 'Approved', 'Rejected', 'Filled', 'On Hold', 'Empty'],
    approval_cycle_type:  ['Direct Manager', 'Hierarchy Reporting Line', 'Dot Reporting Line'],
    job_post_status:      ['Draft', 'Pending', 'Approved', 'Rejected', 'Published'],
  },
  interview_versions: {
    type:   ['VIDEO', 'TEXT', 'FORM', 'INPERSON', 'COVERLETTER', 'SCHEDULED'],
    status: ['Pending', 'Completed', 'Archived'],
  },
  evaluation_forms: {
    status:   ['Pending', 'Completed'],
    decision: ['Recommended', 'Not Recommended', 'Pending'],
  },
  subscriptions: {
    status: ['Limited', 'Active', 'Inactive', 'Cancelling'],
  },
  plans: {
    type:     ['MONTHLY', 'YEARLY', 'FREE'],
    currency: CURRENCIES,
  },
  offer_letters: {
    status:          ['Pending', 'Approved', 'Rejected', 'Expired'],
    employment_type: EMPLOYMENT_TYPES,
    currency:        CURRENCIES,
  },
  onboarding_services: {
    provider: ['PeopleMate', 'Other'],
  },
  add_on_packages: {
    currency: CURRENCIES,
    status:   ['CREATED', 'NOT_PAID', 'ACTIVE', 'INACTIVE', 'CANCELLING'],
  },
  approval_requests: {
    status: ['Requisition Created', 'Draft', 'Pending', 'Approved', 'Rejected', 'Filled', 'On Hold', 'Empty'],
  },
  whatsapp_messages: {
    direction: ['inbound', 'outbound'],
    status:    ['sent', 'delivered', 'read', 'failed'],
    media_type: ['image', 'video', 'document', 'audio'],
  },
  position_assigned_users: {
    assigned_role: ['recruiter', 'hiring_manager', 'reviewer'],
  },
  role_type_definitions: {
    role_type: ['global_administrator', 'administrator', 'organization_administrator', 'hr_manager', 'organization_hr_manager', 'recruiter', 'organization_recruiter', 'hiring_manager', 'organization_hiring_manager', 'organization_user', 'employee'],
  },
  account_feature_overrides: {
    state: ['enabled', 'disabled', 'hidden'],
    feature_key: [
      { group: 'Candidates', items: ['candidates:create','candidates:read_all','candidates:view_history','candidates:request_document','candidates:read','candidates:read_basic_info','candidates:read_basic_skills','candidates:read_extended_info','candidates:read_resume','candidates:read_documents','candidates:read_history','candidates:read_email_history','candidates:read_archive','candidates:send_email','candidates:update','candidates:read_blacklisted','candidates:read_insights'] },
      { group: 'Positions', items: ['positions:create','positions:update','positions:publish','positions:read_all','positions:read','positions:request_document','positions:assign_recruiters'] },
      { group: 'Users', items: ['users:create','users:read_all','users:update','users:delete','users:personal_calendar'] },
      { group: 'Applications', items: ['applications:read_all','applications:update','applications:read_salary','applications:update_salary','applications:create','applications:read_tracker','applications:read_notice','applications:reject','applications:move_stage_confirmation','applications:move_stage_send_notifications','applications:read_status'] },
      { group: 'Interviews', items: ['interviews:create','interviews:read_all','interviews:read_evaluation','interviews:update_evaluation'] },
      { group: 'Requisitions', items: ['requisitions:create','requisitions:read_all','requisitions:read','requisitions:read_history','requisitions:decision','requisitions:read_incoming'] },
      { group: 'Organizations', items: ['organizations:create','organizations:read_all','organizations:read','organizations:update','organizations:assign_users','organizations:user_create','organizations:user_update','organizations:user_read'] },
      { group: 'Offers', items: ['offers:create','offers:read','offers:upsert_approvers'] },
      { group: 'Notes', items: ['notes:read_all','notes:create'] },
      { group: 'Reports', items: ['reports:sourcing','reports:interview_schedule'] },
      { group: 'Roles', items: ['roles:create','roles:update','roles:delete','roles:read_all'] },
      { group: 'Evaluation Forms', items: ['evaluation_forms:read_all'] },
      { group: 'Questions', items: ['questions:create','questions:update','questions:read_all'] },
      { group: 'Job Requests', items: ['job_requests:read','job_requests:approve','job_requests:close','job_requests:reopen'] },
      { group: 'Data Management', items: ['data_management:read'] },
      { group: 'Email Templates', items: ['email_templates:read'] },
      { group: 'Job Templates', items: ['job_templates:read','job_templates:manage'] },
      { group: 'Offer Letter Templates', items: ['offer_letter_templates:read','offer_letter_templates:manage'] },
      { group: 'Recommended Apps', items: ['recommended_applications:read_all','recommended_applications:update','recommended_applications:read','recommended_applications:change_score'] },
      { group: 'Workforce Position', items: ['workforce_position:read_all','workforce_position:read','workforce_position:create','workforce_position:update','workforce_position:delete','workforce_position:read_matrix_manager','workforce_position:read_move_positions','workforce_position:move_branch'] },
      { group: 'Workforce Employee', items: ['workforce_employee:read','workforce_employee:create','workforce_employee:update','workforce_employee:delete'] },
      { group: 'Onboarding', items: ['onboarding_employees:read','onboarding_employees:create','onboarding_employees:update'] },
      { group: 'Trainings', items: ['trainings:read','trainings:create','trainings:update','trainings:archive'] },
      { group: 'Interview Types', items: ['text_interviews','video_interviews','in_person_interviews','cover_letter_interviews'] },
      { group: 'LinkedIn', items: ['linkedin_searches'] },
      { group: 'Company', items: ['company:read','company:dashboard','company:template','company:career_site','company:access_keys'] },
    ],
  },
}

// ─── Country select fields ────────────────────────────────────────────────────

/**
 * Field names (any resource) that should render as a country select.
 */
export const COUNTRY_FIELD_NAMES = new Set(['country'])

// ─── Multiselect / tag-input fields per resource ──────────────────────────────

/**
 * Fields that hold an array of values and should render as a tag/chip input.
 * value type: string array serialised as JSON, or a plain array from PostgREST.
 */
export const ARRAY_FIELDS: Record<string, string[]> = {
  admins:           ['permissions'],
  agencies:         ['candidate_notification_methods'],
  companies:        ['working_locations', 'auto_saved_questions', 'competencies'],
  local_candidates: ['skills', 'soft_skills', 'links'],
  candidate_educations: ['field_of_study', 'graduation_project_description'],
  candidate_courses:    ['description'],
  candidate_projects:   ['description'],
  candidate_data_transferred_fields: ['selected_fields'],
  company_positions: ['skills', 'soft_skills', 'calendar_user_ids', 'certificates', 'knowledge', 'licenses', 'language'],
  requisitions:     ['skills', 'certificates', 'knowledge', 'licenses', 'language', 'approval_requests_order'],
  interview_versions: ['calendar_user_ids', 'available_dates'],
  role_type_definitions: ['applicableCompanyTypes'],
  org_chart_positions: ['skills', 'certificates', 'knowledge', 'licenses'],
}

/**
 * Like ARRAY_FIELDS but each tag must come from a predefined option list.
 * Key: resource → field → options[].
 */
export const ARRAY_ENUM_FIELDS: Record<string, Record<string, string[]>> = {
  agencies: {
    candidate_notification_methods: ['email', 'phone'],
  },
  companies: {
    working_locations: COUNTRIES,
  },
  role_type_definitions: {
    applicableCompanyTypes: ['normal', 'agency', 'organization'],
  },
}

/**
 * Fields backed by a JSON/JSONB column. Rendered as a JSON textarea and
 * parsed back to an object on submit (prevents double-encoded string storage).
 */
export const JSON_FIELDS: Record<string, string[]> = {
  company_roles: ['permissions'],
}

/** Fields that should render as a masked password input. */
export const PASSWORD_FIELDS: Record<string, string[]> = {
  users:       ['password'],
  job_seekers: ['password'],
  company_email_configuration: ['mail_password'],
}

/**
 * Salt fields that are auto-generated when the password changes.
 * These fields are hidden from the user and auto-populated.
 */
export const SALT_FIELDS: Record<string, string[]> = {
  users:       ['salt'],
  job_seekers: ['salt'],
}

/** Fields that should render as a datetime-local input. */
export const DATETIME_FIELDS: Record<string, string[]> = {
  account_feature_overrides: ['expires_at'],
}

/**
 * Fields that should never appear in the form (auto-managed or irrelevant).
 */
export const HIDDEN_FIELDS: Record<string, string[]> = {
  users:       ['salt'],
  job_seekers: ['salt', 'reset_token', 'token_expiry', 'last_reset_request', 'reset_request_count',
                'verification_code', 'verification_expiry', 'last_verification_code_request',
                'verification_request_count'],
  local_candidates: ['ai_formatted_data', 'cv_insights'],
  company_positions: ['ai_formatted_data', 'invitation_link', 'internal_invitation_link', 'easy_apply_link'],
  interview_versions: ['ordered_recording_ids', 'interviewer_link', 'interviewee_link', 'viewer_link'],
  evaluation_forms: ['dynamic_responses'],
}

/** Fields that should render as a numeric input. */
export const NUMBER_FIELDS: Record<string, string[]> = {
  company_email_configuration: ['mail_port'],
  company_positions: ['years_of_experience', 'max_years_of_experience', 'number_of_vacancies',
                      'budget', 'additional_salary', 'scheduler_duration'],
  requisitions:      ['years_of_experience', 'max_years_of_experience', 'number_of_vacancies',
                      'budget', 'additional_salary'],
  plans:             ['duration', 'expiration_duration', 'price', 'positions', 'text_interviews',
                      'video_interviews', 'in_person_interviews', 'cover_letter_interviews',
                      'users', 'organizations', 'candidates'],
  subscriptions:     ['positions', 'text_interviews', 'video_interviews', 'in_person_interviews',
                      'cover_letter_interviews', 'candidates', 'users', 'organizations'],
  plan_features:     ['limit_count'],
  plan_limits:       ['limit_value'],
  account_feature_overrides: ['limit_override', 'cover_letter_interviews', 'candidates', 'users', 'organizations',
                      'linkedin_searches'],

  voice_over_rate_limits: ['requests_count'],
  voice_over_audit_logs:  ['character_count', 'processing_time_ms'],
}

/** Fields that should render as a multi-line textarea. */
export const TEXTAREA_FIELDS: Record<string, string[]> = {
  company_positions: ['description', 'requirements', 'key_words', 'competencies',
                      'freelancing_reason', 'project_duration'],
  requisitions:      ['description', 'requirements', 'key_words', 'competencies',
                      'freelancing_reason', 'closure_notes', 'closure_reason_other'],
  local_candidates:  ['summary'],
  evaluation_forms:  ['strengths_comment', 'improvements_comment', 'general_comment'],
  offer_letters:     ['offer_template'],
  feature_flags:     ['description'],
  account_feature_overrides: ['reason'],
  company_templates: ['questions'],
  position_questions: ['question', 'answer', 'competency'],
  whatsapp_messages:  ['body', 'error_message'],
  greenhouse_request_errors: ['errors'],
  onboarding_service_transfer_history: ['transferred_data', 'response_json'],
}

/**
 * Explicit field order for resources that may have zero rows (empty tables).
 * Drives both the CREATE form (when no sample row exists) and display order.
 * Also acts as the schema definition for resources unknown from a sample.
 * Extend this when adding new resources to ensure they always render correctly.
 */
export const RESOURCE_FIELD_ORDER: Record<string, string[]> = {
  company_email_configuration: [
    'company_id', 'mail_server', 'mail_port', 'mail_username', 'mail_password',
    'mail_default_sender', 'mail_use_ssl', 'mail_use_tls',
  ],
  position_questions: [
    'position_id', 'requisition_id', 'question', 'answer', 'template_id',
    'created_by', 'added_by_ai', 'competency',
  ],
  add_on_packages: ['service_type', 'quantity_price_package', 'currency', 'status'],
  offer_letters: [
    'company_id', 'position_id', 'position_stage_id', 'application_id', 'candidate_id',
    'created_by', 'status', 'compensation_amount', 'offer_expiry_date',
    'expected_joining_date', 'employment_type', 'currency', 'attachment', 'offer_template',
  ],
  partner_api_keys: ['customer_name', 'api_key_hash', 'api_key_encrypted', 'company_id'],
  partner_assessments: ['partner_test_name', 'interview_type', 'api_key_id'],
  greenhouse_request_errors: [
    'api_call', 'errors', 'partner_test_id', 'partner_test_name',
    'partner_interview_id', 'candidate_email',
  ],
  onboarding_services: [
    'company_id', 'service_name', 'provider', 'base_url', 'data_api',
    'auth_api', 'app_api_key', 'api_header', 'is_active',
  ],
  onboarding_service_transfer_history: [
    'onboarding_service_id', 'candidate_id', 'application_id', 'stage_id',
    'transfer_status', 'transferred_data', 'response_json',
  ],
  organization_assigned_users: ['user_id', 'organization_id', 'agency_id'],
  whatsapp_messages: [
    'company_id', 'candidate_id', 'application_id', 'stage_id', 'agency_id',
    'direction', 'status', 'template_name', 'body', 'media_type', 'media_url',
    'error_code', 'error_message',
  ],
  blacklisted_emails: ['email_domain', 'reason', 'is_active'],
  company_templates: ['name', 'industry', 'questions', 'created_by', 'company_id'],
  users: [
    'username', 'email', 'fullname', 'password', 'phone', 'picture',
    'email_verified', 'active',
  ],
  agencies: ['name', 'type', 'candidate_notification_methods', 'job_seeker_registration_method'],
  services_count: [
    'organization_id', 'organization_name', 'agency_id',
    'positions', 'text_interviews', 'video_interviews', 'cover_letter_interviews',
    'in_person_interviews', 'candidates', 'matched_candidates', 'users',
  ],
  feature_flags: ['key', 'enabled', 'description'],
  plan_features: ['plan_id', 'feature_key', 'state', 'limit_count'],
  plan_limits: ['plan_id', 'limit_key', 'limit_value'],
  account_feature_overrides: ['company_id', 'feature_key', 'state', 'limit_override', 'reason'],
  role_type_definitions: ['id', 'role_type', 'applicableCompanyTypes'],
  permission_change_audits: ['company_id', 'actor_user_id', 'target_type', 'target_id', 'action', 'before_state', 'after_state', 'would_have_blocked', 'created_at'],
}

/**
 * Field names ending in `_id` that are NOT foreign keys and must render
 * as a plain text/number input rather than an FK select.
 */
export const PLAIN_ID_FIELDS = new Set([
  'national_id',
  'people_mate_vacancy_id',
  'greenhouse_job_id',
  'whatsapp_message_id',
  'agency_candidate_id',
  'dynamic_template_id',
  'api_key_id',
  'partner_test_id',
  'partner_interview_id',
  'in_reply_to',
  'target_id',
])

export const RESOURCE_DESCRIPTIONS: Record<string, string> = {
  account_feature_overrides:
    'Per-company exceptions that override subscription limits or feature states. Each row targets ONE company and ONE feature key. Overrides take priority over everything else. Set an expires_at to make it temporary. Feature keys match subscription limit keys: users, candidates, positions, text_interviews, video_interviews, in_person_interviews, cover_letter_interviews, organizations, linkedin_searches.',
  role_type_definitions:
    'Controls which company types can use each role type. Organization-* roles are only for organizations. Normal/agency roles only work for non-org companies.',
}

/** Resolve the field type for a given resource + field combination. */
export type FieldKind =
  | 'boolean'
  | 'password'
  | 'number'
  | 'textarea'
  | 'country'
  | 'array'
  | 'array_enum'
  | 'enum'
  | 'fk'
  | 'plain_id'
  | 'hidden'
  | 'datetime'
  | 'json'
  | 'text'

export function resolveFieldKind(resource: string, field: string): FieldKind {
  if (HIDDEN_FIELDS[resource]?.includes(field)) return 'hidden'
  if (SALT_FIELDS[resource]?.includes(field)) return 'hidden'
  if (BOOLEAN_FIELDS[resource]?.includes(field)) return 'boolean'
  if (PASSWORD_FIELDS[resource]?.includes(field)) return 'password'
  if (NUMBER_FIELDS[resource]?.includes(field)) return 'number'
  if (TEXTAREA_FIELDS[resource]?.includes(field)) return 'textarea'
  if (DATETIME_FIELDS[resource]?.includes(field)) return 'datetime'
  if (JSON_FIELDS[resource]?.includes(field)) return 'json'
  if (COUNTRY_FIELD_NAMES.has(field)) return 'country'
  if (ARRAY_ENUM_FIELDS[resource]?.[field]) return 'array_enum'
  if (ARRAY_FIELDS[resource]?.includes(field)) return 'array'
  if (ENUM_FIELDS[resource]?.[field]) return 'enum'
  if (PLAIN_ID_FIELDS.has(field)) return 'plain_id'
  return 'text'
}
